import { Controller } from '@hotwired/stimulus'

// A plan row's amount: one edit, one save, and a row that cannot be edited while it saves.
//
// 🔴 Enter commits the field instead of submitting the form. The browser's own submit sent the
// amount at once, and the `change` the same Enter fired sent it again through `submit-on-change`'s
// debounce — two requests for one edit. Blurring fires that `change`, so Enter takes the one path
// every edit already takes: debounced, clamped by Bali's stepper first, folded with any press that
// follows.
//
// While a save is in flight the form is `inert` — no clicks, keys or focus — and Turbo marks it
// `aria-busy`, which the view's spinner reads. Bali's `submit-button` cannot do this: it spins the
// SUBMITTER, and `requestSubmit()` from `submit-on-change` has none.
export default class extends Controller {
  commit (event) {
    event.preventDefault()
    event.target.blur()
  }

  // 🔴 A submit while one is in flight is dropped. It adds nothing — the request in flight read
  // every control, and none could change since — and Turbo would abort that request to send this
  // one. The case is real: an amount's debounced save still pending when the station picker
  // submits at once.
  hold (event) {
    if (this.element.hasAttribute('inert')) event.preventDefault()
  }

  // `inert`, not `disabled`: a disabled control drops out of the form's data, and `disabled` is
  // also Bali's own mark for a stepper button at its limit, which this must not take over.
  busy () {
    if (this.element.hasAttribute('inert')) return

    if (this.element.contains(document.activeElement)) this.focused = document.activeElement
    this.element.setAttribute('inert', '')
  }

  // Runs at the end of every save. A saved or refused row is then redrawn whole, which takes the
  // focus with it; a response that leaves the row in place — a 403, an error page, a dropped
  // connection — gets it back, unless the planner has already moved to another row.
  // ⚠️ So after a SUCCESSFUL save a keyboard user lands on the page, not the row.
  //
  // Also before Turbo caches the page, so a row snapshotted mid-save is not restored inert.
  idle () {
    this.element.removeAttribute('inert')
    if (this.focused?.isConnected && document.activeElement === document.body) this.focused.focus()
    this.focused = null
  }
}
